import { List, type OrderedMap } from '@gitbook/util-immutable';

import { isType, MODEL_TYPES } from '../constants';
import { generateKey } from '../utils';
import type { Block } from './block';
import { AbstractNode, memoizeMethods } from './node';

export type FragmentMap = OrderedMap<string, Fragment>;

export interface FragmentProperties {
    key: string;
    fragment?: string;
    type?: string;
    nodes: List<Block>;
}

/**
 * Fragment node in an editor value. A Fragment is essentially a Document,
 * except that it cannot handle its own children creation. These children
 * must be created by the parent node, and passed down.
 */
export class Fragment extends AbstractNode {
    public declare readonly data: never;
    public declare readonly fragments: never;
    public declare readonly isVoid: never;

    /**
     * Unique key for the fragment node in the document.
     * This key may differ from the one used in the parent.fragments map.
     */
    public declare readonly key: string;

    /**
     * Nickname for the fragment in its parent.
     * if defined, it'll be used as a key in the parent's fragments map.
     * If not defined, the key will be used
     */
    public declare readonly fragment: string | undefined;

    /**
     * Inner block content of the fragment.
     */
    public declare readonly nodes: List<Block>;

    /**
     * Optional type of the fragment to identify and normalize multiple fragments.
     */
    public declare readonly type: string | undefined;

    get object(): 'fragment' {
        return 'fragment';
    }

    /**
     * Return the local key used in the fragments mapping.
     */
    get fragmentKey(): string {
        return this.fragment || this.key;
    }

    /**
     * Check if the fragment is empty.
     * Returns true if all it's children nodes are empty.
     */
    get isEmpty(): boolean {
        return !this.nodes.some((child) => !child.isEmpty);
    }

    /**
     * Get the concatenated text of all the document's children.
     */
    get text(): string {
        return this.getText();
    }

    /**
     * Check if `input` is a `Fragment`.
     */
    public static isFragment(input: any): input is Fragment {
        return isType('FRAGMENT', input);
    }

    /*
     * Implemented here to adhere to protect against accidental usage.
     */
    public static create(
        attrs: Partial<Omit<FragmentProperties, 'nodes'> & { nodes: Block[] | List<Block> }> = {}
    ): Fragment {
        const key = attrs.key || generateKey();
        // @ts-expect-error
        return Fragment.createRecord({
            fragment: key,
            ...attrs,
            key,
            nodes: List.isList(attrs.nodes) ? attrs.nodes : List.create(attrs.nodes || []),
        });
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Fragment.prototype[MODEL_TYPES.FRAGMENT] = true;

memoizeMethods(Fragment);
